import { useCallback } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import { useApolloClient } from '@apollo/client';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useExpandCollapseRow from 'framework-listing-lib/hooks/useExpandCollapseRow';
import useCrmObjectTypeListingFilters from 'framework-listing-lib/hooks/useCrmObjectTypeListingFilters';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { CRM_OBJECT_TYPE_FIELD_NAME } from 'framework-listing-lib/internal/hooks/useFetchFromCRM';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import useUIMode, { useOnChangeUIMode, useCurrentUIMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { UI_MODE } from 'framework-listing-lib/constants';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import useHasGraphQLCrmSearch from 'framework-listing-lib/internal/graphql/hooks/useHasGraphQLCrmSearch';
import { CRM_SEARCH_GRAPHQL_CACHE_KEY } from 'framework-listing-lib/internal/graphql/constants';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ListingLibUIModeSelect() {
  const hasViews = useHasViews();
  const uiModeProps = useUIMode();
  const currentUIMode = useCurrentUIMode();
  const onChangeUIMode = useOnChangeUIMode();
  const client = useDataFetchingClient();
  const apolloClient = useApolloClient();
  const {
    collapseAll
  } = useExpandCollapseRow();
  const {
    onClearFilters
  } = useCrmObjectTypeListingFilters();
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const hasGraphQLCrmSearch = useHasGraphQLCrmSearch();
  const wipeTableCache = useCallback(() => {
    if (hasGraphQLCrmSearch) {
      apolloClient.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: CRM_SEARCH_GRAPHQL_CACHE_KEY
      });
      apolloClient.cache.gc();
    } else {
      // Clear all cache keys for @fieldName
      client.cache.evict({
        fieldName: CRM_OBJECT_TYPE_FIELD_NAME
      });
      client.cache.gc();
    }
  }, [apolloClient.cache, client.cache, hasGraphQLCrmSearch]);
  const handleUIModeChange = useCallback(newUIMode => () => {
    if (currentUIMode === newUIMode) {
      return;
    }
    listingLibObserver.changeUIMode(newUIMode);
    trackCrmTableInteraction({
      action: 'change-ui-mode',
      newUIMode
    });
    collapseAll();
    onClearFilters({
      newUIMode
    });
    wipeTableCache();
    onChangeUIMode(newUIMode);
  }, [collapseAll, currentUIMode, onChangeUIMode, onClearFilters, trackCrmTableInteraction, wipeTableCache]);
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "crm-listing-ui-mode-select",
    children: /*#__PURE__*/_jsx(ButtonGroup, {
      marginInlineEnd: "300",
      marginInlineStart: hasViews && uiModeProps.currentUIMode !== UI_MODE.FOLDERS ? '300' : '0',
      children: [UI_MODE.LIST, UI_MODE.FOLDERS].map(uiMode => /*#__PURE__*/_jsx(Button, {
        "data-test-id": "crm-listing-ui-mode-button",
        "data-test-ui-mode": uiMode,
        onClick: handleUIModeChange(uiMode),
        size: "small",
        use: "tertiary-extra-light",
        active: uiModeProps.currentUIMode === uiMode,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `frameworkListingLib.uiModes.${uiMode}`
        })
      }, uiMode))
    })
  });
}